// A leaf reader shared by zone crossings and movement pricing. Keep this independent of the walk resolver:
// expiry and water status handling also import difficult-terrain during an AOE expansion.
import { ZoneHandBack, type Zone, type ZoneReaders } from './zone-membership.js';
import { selfAoeEffectiveDistanceFt } from '../../../../ft3d/engine/ft3d-combat-distance-core.js';
import { classAuraEffectiveRadius } from '../statuses/class-aura.js';
import { isSelfStatusOrigin } from '../statuses/status-origin.js';
import type { WallMovementProfile } from '../../shared/painted-wall-rules.js';
import { SHAPES } from '../ability/aoe/shape-ids.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(v => record(v) !== null) as Row[] : [];
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const f = (value: unknown): number => { const n = Number(Array.isArray(value) ? value[0] : value); return Number.isFinite(n) ? n : 0; };
const entityId = (row: Row): string => upper(record(row.encounter_creatures)?.encounter_creature_id);
const targetStatuses = (row: Row): Row[] => rows(record(row.statuses)?.target);

export interface WalkZoneReaders extends ZoneReaders {
  movementProfile?: (row: Row) => WallMovementProfile | undefined;
  definition: (id: string) => Row | null;
  has: (definition: Row, layer: string) => boolean;
  layer: (row: Row, layer: string) => unknown;
  combatSizeFt: (row: Row) => number | null | undefined;
}

const SELF_RANGE_ID = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
/** Current class-aura radius, including the carrier's folded modifier and centered Self size credit. */
export function classAuraExpectedStored(definition: Row, carrier: Row, read: WalkZoneReaders): number | undefined {
  const authored = record(definition.item_layer_types)?.aoe_distance ?? read.layer({ ...definition, computed: undefined }, 'aoe_distance');
  const modifiers = record(record(record(carrier.computed)?.creature)?.modifiers);
  const folded = modifiers?.aura_distance_mod;
  const mod = folded !== undefined && folded !== null && folded !== '' ? folded : read.layer(carrier, 'aura_distance_mod');
  const effective = classAuraEffectiveRadius(Array.isArray(authored) ? authored[0] : authored, Array.isArray(mod) ? mod[0] : mod ?? 0);
  if (upper(read.layer(definition, 'range')) !== SELF_RANGE_ID) return effective;
  const shape = SHAPES[upper(read.layer(definition, 'aoe_type'))] ?? '';
  if (!['sphere', 'circle', 'cylinder', 'cube', 'square'].includes(shape)) return effective;
  const size = read.combatSizeFt(carrier);
  if (size === undefined) return undefined;
  return selfAoeEffectiveDistanceFt(effective, size, shape);
}

/** A self-cast row elides status_caster_id at intern; a stored empty caster stays empty. */
export const statusCasterId = (row: Row): string => upper('status_caster_id' in row ? row.status_caster_id : row.status_target_id);
const isClassAura = (def: Row, read: WalkZoneReaders): boolean =>
  !['aoe_type', 'aoe_distance', 'persistent_aoe', 'aoe_follows_target'].some(k => read.layer(def, k) == null) && f(read.layer(def, 'class_aura')) === 1;
const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

function classAuraAnchor(carrier: Row, read: WalkZoneReaders): { row: Row; def: Row; refreshes: boolean } | null {
  const id = entityId(carrier);
  for (const row of targetStatuses(carrier)) {
    if (f(row.status_deleted) === 1 || f(row.status_aoe_source) !== 1) continue;
    if (statusCasterId(row) !== id || upper(row.status_target_id) !== id) continue;
    let def = record(row.status_arr);
    if (!def) {
      const ref = upper(row.status_item_id ?? (typeof row.status_arr === 'string' ? row.status_arr : ''));
      if (UUID_RE.test(ref)) { def = read.definition(ref); if (!def) throw new ZoneHandBack('persistent-zone:class-aura-definition'); }
    }
    if (def) { if (isClassAura(def, read)) return { row, def, refreshes: true }; continue; }
    const origin = upper(row.status_origin_id || row.status_item_id);
    if (origin === '' || isSelfStatusOrigin(upper(row.status_id), origin)) continue;
    const fallback = read.definition(origin);
    if (!fallback) throw new ZoneHandBack('persistent-zone:class-aura-definition');
    if (isClassAura(fallback, read)) return { row, def: fallback, refreshes: false };
  }
  return null;
}

/** Judge class auras at the carrier's current radius without mutating held anchor rows. */
export function refreshClassAuraZones(zones: Zone[], creatures: Row[], read: WalkZoneReaders,
  options: { lenient?: boolean; skip?: string } = {}): Zone[] {
  const radius = new Map<string, number>();
  const holders = new Set(zones.filter(z => z.dynamic).map(z => upper(z.holderId)));
  for (const carrier of creatures) {
    const id = entityId(carrier);
    if (!holders.has(id) || (options.skip !== undefined && id === upper(options.skip))) continue;
    try {
      const anchor = classAuraAnchor(carrier, read);
      if (!anchor || !anchor.refreshes) continue;
      const expected = classAuraExpectedStored(anchor.def, carrier, read);
      if (expected === undefined) throw new ZoneHandBack('persistent-zone:class-aura-size');
      if (Math.abs(f(anchor.row.status_aoe_distance) - expected) >= 0.001) radius.set(upper(anchor.row.status_id), expected);
    } catch (error) { if (!options.lenient) throw error; }
  }
  if (!radius.size) return zones;
  return zones.map(zone => {
    const refreshed = radius.get(upper(zone.status.status_id));
    return refreshed === undefined ? zone : { ...zone, status: { ...zone.status, status_aoe_distance: refreshed } };
  });
}
